import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Badge } from '../components/ui/Badge';
import { useEvent, useUser } from '../store';
import { formatCurrency, loadEvent as loadEventFromStorage, saveEvent, mergeTemplateIntoEvent, canManageFights, canEnterResults, canManageParticipants, runBetResolutionTests } from '../utils';
import { getCurrentUser as getCurrentParticipantIdForEvent } from '../utils/persistence';
import { useAuth } from '../store';
import { ShareableLinkDisplay } from '../components/sharing';
import { useEventPolling } from '../hooks';
import { deleteEvent, addToPublicEvents, removeFromPublicEvents, saveTemplate, finishTemplateAndAllEvents, templateHasResults, loadTemplate } from '../services/firestore';
import { isUltraAdmin } from '../utils';

/**
 * Admin Panel Page
 * Main page for event administration
 */
export function AdminPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { event, loadEvent, fights, participants, bets, updateEvent } = useEvent();
  const { isAdmin, setIsAdmin } = useUser();
  const { currentUser } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'fights' | 'participants' | 'bets'>('overview');
  const [selectedFightForDetails, setSelectedFightForDetails] = useState<string | null>(null);
  const [selectedFightForBetAnalysis, setSelectedFightForBetAnalysis] = useState<string | null>(null);

  // Helper to save event or template depending on type
  const saveEventOrTemplate = async (eventData: typeof event) => {
    if (!eventData) return;
    if (eventData.isTemplate) {
      await saveTemplate(eventData);
    } else {
      await saveEvent(eventData);
    }
  };

  // Load event from localStorage
  useEffect(() => {
    if (!eventId) {
      navigate('/');
      return;
    }

    const loadEventData = async () => {
      setLoading(true);
      
      try {
        let eventData = await loadEventFromStorage(eventId);
        if (eventData) {
          // Merge template fights for event instances
          const mergedEventData = await mergeTemplateIntoEvent(eventData);
          loadEvent(mergedEventData);
          
          // Check if current user is ultra-admin
          if (currentUser && isUltraAdmin(currentUser)) {
            setIsAdmin(true);
          } else {
            // Check if current user is admin of this event
            const currentParticipantId = getCurrentParticipantIdForEvent(eventId);
            if (currentParticipantId === mergedEventData.adminId) {
              setIsAdmin(true);
            } else {
              // Also check by username match
              const adminParticipant = mergedEventData.participants.find(p => p.id === mergedEventData.adminId);
              if (adminParticipant && currentUser && 
                  adminParticipant.name.toLowerCase() === currentUser.username.toLowerCase()) {
                setIsAdmin(true);
              }
            }
          }
        } else {
          navigate('/');
        }
      } catch (err) {
        console.error('Error loading event:', err);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    loadEventData();
  }, [eventId, loadEvent, navigate, setIsAdmin, currentUser]);

  // Redirect if not admin
  useEffect(() => {
    if (!loading && event && !isAdmin && eventId && currentUser) {
      // Check if user is ultra-admin
      if (isUltraAdmin(currentUser)) {
        setIsAdmin(true);
        return;
      }
      
      // Check if user is admin of this event
      const currentParticipantId = getCurrentParticipantIdForEvent(eventId);
      const adminParticipant = event.participants.find(p => p.id === event.adminId);
      const isUserAdmin = currentParticipantId === event.adminId || 
                         (adminParticipant && adminParticipant.name.toLowerCase() === currentUser.username.toLowerCase());
      
      if (!isUserAdmin) {
        navigate(`/event/${eventId}`);
      }
    }
  }, [loading, event, isAdmin, eventId, navigate, currentUser, setIsAdmin]);

  // Poll for event updates (every 5 seconds)
  useEventPolling({
    eventId: eventId || null,
    enabled: !loading && !!eventId && isAdmin,
    interval: 5000,
    onUpdate: (_updatedEvent) => {
      // Event updated - state will be updated automatically by the hook
      console.log('Event updated via polling (admin)');
    },
    onError: (error) => {
      console.error('Error polling event:', error);
    },
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-secondary-text">Loading...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return null; // Will redirect
  }

  const totalBets = bets.length;
  const totalBetAmount = bets.reduce((sum, bet) => sum + bet.amount, 0);
  const totalParticipants = participants.length;
  // Permission checks
  const userIsUltraAdmin = currentUser ? isUltraAdmin(currentUser) : false;
  const canUserManageFights = event ? canManageFights(event, userIsUltraAdmin) : false;
  const canUserEnterResults = event ? canEnterResults(event, userIsUltraAdmin) : false;
  const canUserManageParticipants = event ? canManageParticipants(event) : false;

  // Handler functions
  const handlePublishEvent = async () => {
    if (!event) return;
    
    const isCurrentlyPublished = event.published ?? true; // Treat undefined as published (backward compatibility)
    
    const confirmation = window.confirm(
      isCurrentlyPublished 
        ? 'Are you sure you want to unpublish this event? It will become inaccessible to participants.'
        : 'Are you sure you want to publish this event? Participants will be able to access it.'
    );
    
    if (confirmation) {
      const updatedEvent = {
        ...event,
        published: !isCurrentlyPublished,
      };
      
      // If publishing and event is public, add to public events
      if (updatedEvent.published && updatedEvent.isPublic) {
        await addToPublicEvents(updatedEvent.id);
      }
      // If unpublishing, remove from public events
      else if (!updatedEvent.published) {
        await removeFromPublicEvents(updatedEvent.id);
      }
      
      updateEvent(updatedEvent);
      await saveEventOrTemplate(updatedEvent);
    }
  };

  const handleDeleteEvent = async () => {
    if (!event || !eventId) return;
    
    // Prevent deleting templates
    if (event.isTemplate) {
      alert('Cannot delete templates from here. Please use the Templates page to delete templates.');
      return;
    }
    
    // Check if template has started (has any results)
    if (event.templateId) {
      const hasResults = await templateHasResults(event.templateId);
      if (hasResults) {
        alert('Cannot delete this event because the template has started (results have been entered). Once betting has started, events cannot be deleted to protect the integrity of the game.');
        return;
      }
    }
    
    const confirmation = window.confirm(
      'Are you sure you want to DELETE this event? This action CANNOT be undone. All data including bets, participants, and fights will be permanently deleted.'
    );
    
    if (confirmation) {
      const secondConfirmation = window.confirm(
        'FINAL WARNING: This will permanently delete the event. Type OK in your mind and click OK to proceed.'
      );
      
      if (secondConfirmation) {
        await deleteEvent(eventId);
        await removeFromPublicEvents(eventId);
        navigate('/games');
      }
    }
  };

  const handleFinishTemplate = async () => {
    if (!event || !event.isTemplate) return;
    
    const confirmation = window.confirm(
      'Are you sure you want to finish this template? This will also finish ALL events using this template. This action cannot be undone.'
    );
    
    if (confirmation) {
      const success = await finishTemplateAndAllEvents(event.id);
      if (success) {
        alert(`Template and all associated events have been finished successfully.`);
        
        // Reload the template to show updated state
        const updatedTemplate = await loadTemplate(event.id);
        if (updatedTemplate) {
          loadEvent(updatedTemplate);
        }
      } else {
        alert('Failed to finish template. Please try again.');
      }
    }
  };

  const handleDeleteFight = async (fightId: string) => {
    if (!event) return;
    
    const fight = fights.find(f => f.id === fightId);
    if (!fight) return;
    
    // Check if any bets have been placed on this fight
    const fightBets = bets.filter(b => 
      b.fightId === fightId || 
      (b.isParlay && b.parlayLegs?.some(leg => leg.fightId === fightId))
    );
    
    let confirmation;
    if (fightBets.length > 0) {
      confirmation = window.confirm(
        `WARNING: This fight has ${fightBets.length} bet(s) placed on it (including parlays). Deleting this fight will REFUND all bet amounts to participants. Are you sure you want to continue?`
      );
    } else {
      confirmation = window.confirm(
        `Are you sure you want to delete the fight between ${fight.fighters[0]?.name || 'TBD'} vs ${fight.fighters[1]?.name || 'TBD'}?`
      );
    }
    
    if (confirmation) {
      // Remove fight from event
      const updatedFights = event.fights.filter(f => f.id !== fightId);
      
      // Remove all bets for this fight and REFUND the amounts to participants
      const updatedParticipants = event.participants.map(p => {
        let refundToStake = 0;
        let refundToWinnings = 0;
        
        // Filter out bets for this fight and calculate refunds
        const remainingBets = p.bets.filter(b => {
          const shouldRemove = b.fightId === fightId || 
                              (b.isParlay && b.parlayLegs?.some(leg => leg.fightId === fightId));
          
          if (shouldRemove) {
            // Determine where to refund based on bet status
            if (b.status === 'pending') {
              // Pending bet - refund to wherever it came from
              // Check if participant had initial stake remaining when this bet was placed
              // For simplicity, refund to initial stake first up to $1000 total
              const currentInitialStake = p.initialStakeRemaining + refundToStake;
              if (currentInitialStake < 1000) {
                const canRefundToStake = Math.min(b.amount, 1000 - currentInitialStake);
                refundToStake += canRefundToStake;
                refundToWinnings += b.amount - canRefundToStake;
              } else {
                refundToWinnings += b.amount;
              }
            } else if (b.status === 'won' && b.payout) {
              // Won bet - remove winnings, refund original bet to stake
              refundToWinnings -= (b.payout - b.amount); // Remove profit
              const currentInitialStake = p.initialStakeRemaining + refundToStake;
              if (currentInitialStake < 1000) {
                const canRefundToStake = Math.min(b.amount, 1000 - currentInitialStake);
                refundToStake += canRefundToStake;
                refundToWinnings += b.amount - canRefundToStake;
              } else {
                refundToWinnings += b.amount;
              }
            } else if (b.status === 'lost') {
              // Lost bet - refund the amount
              const currentInitialStake = p.initialStakeRemaining + refundToStake;
              if (currentInitialStake < 1000) {
                const canRefundToStake = Math.min(b.amount, 1000 - currentInitialStake);
                refundToStake += canRefundToStake;
                refundToWinnings += b.amount - canRefundToStake;
              } else {
                refundToWinnings += b.amount;
              }
            }
          }
          
          return !shouldRemove;
        });
        
        return {
          ...p,
          bets: remainingBets,
          initialStakeRemaining: p.initialStakeRemaining + refundToStake,
          winningsBalance: p.winningsBalance + refundToWinnings,
          totalAvailable: p.totalAvailable + refundToStake + refundToWinnings,
        };
      });
      
      const updatedEvent = {
        ...event,
        fights: updatedFights,
        participants: updatedParticipants,
      };
      
      updateEvent(updatedEvent);
      await saveEventOrTemplate(updatedEvent);
      
      const totalRefund = fightBets.reduce((sum, b) => sum + b.amount, 0);
      console.log(`Fight deleted. Refunded ${fightBets.length} bets totaling ${formatCurrency(totalRefund)}`);
    }
  };

  const handleRemoveResult = async (fightId: string) => {
    if (!event) return;
    
    const fight = fights.find(f => f.id === fightId);
    if (!fight) return;
    
    const confirmation = window.confirm(
      `Are you sure you want to REMOVE the result for ${fight.fighters[0]?.name} vs ${fight.fighters[1]?.name}? All bets (including parlays) will return to pending status.`
    );
    
    if (confirmation) {
      // Find all bets for this fight (including parlays that contain this fight)
      const fightBets = bets.filter(b => 
        b.fightId === fightId || 
        (b.isParlay && b.parlayLegs?.some(leg => leg.fightId === fightId))
      );
      
      // Update all participants' bets to set this fight's bets back to pending
      const updatedParticipants = participants.map(p => {
        let updatedWinnings = p.winningsBalance;
        let updatedAvailable = p.totalAvailable;
        
        const updatedBets = p.bets.map(b => {
          // Handle regular bets for this fight
          if (b.fightId === fightId) {
            // If this was a winning bet, subtract the winnings
            if (b.status === 'won' && b.payout) {
              updatedWinnings -= b.payout;
              updatedAvailable -= b.payout;
            }
            
            // If this was a losing bet, refund the amount
            if (b.status === 'lost') {
              updatedWinnings += b.amount;
              updatedAvailable += b.amount;
            }
            
            // Revert bet to pending
            return { ...b, status: 'pending' as const };
          }
          
          // Handle parlay bets that include this fight
          if (b.isParlay && b.parlayLegs) {
            const hasThisFight = b.parlayLegs.some(leg => leg.fightId === fightId);
            if (hasThisFight) {
              // If parlay was won or lost, revert it
              if (b.status === 'won' && b.payout) {
                updatedWinnings -= b.payout;
                updatedAvailable -= b.payout;
              }
              
              if (b.status === 'lost') {
                updatedWinnings += b.amount;
                updatedAvailable += b.amount;
              }
              
              // Set all legs back to pending
              const updatedLegs = b.parlayLegs.map(leg => ({
                ...leg,
                status: 'pending' as const
              }));
              
              return { 
                ...b, 
                status: 'pending' as const,
                parlayLegs: updatedLegs
              };
            }
          }
          
          return b;
        });
        
        return {
          ...p,
          bets: updatedBets,
          winningsBalance: updatedWinnings,
          totalAvailable: updatedAvailable
        };
      });
      
      // Update the fight to remove result and set back to upcoming
      // Use null instead of undefined so Firestore actually removes the field
      console.log('[handleRemoveResult] event.fights count:', event.fights?.length);
      console.log('[handleRemoveResult] Looking for fight:', fightId);
      
      const updatedFights = event.fights.map(f => {
        if (f.id === fightId) {
          console.log('[handleRemoveResult] Found fight, removing result. Old status:', f.status, 'Old result:', f.result);
          return { ...f, status: 'upcoming' as const, result: null };
        }
        return f;
      });
      
      const fightAfterUpdate = updatedFights.find(f => f.id === fightId);
      console.log('[handleRemoveResult] Fight after update:', fightAfterUpdate?.status, fightAfterUpdate?.result);
      
      const updatedEvent = {
        ...event,
        fights: updatedFights,
        participants: updatedParticipants,
      };
      
      console.log('[handleRemoveResult] Saving event. isTemplate:', updatedEvent.isTemplate);
      console.log('[handleRemoveResult] Updated fights count:', updatedEvent.fights?.length);
      
      updateEvent(updatedEvent);
      const saveResult = await saveEventOrTemplate(updatedEvent);
      
      console.log('[handleRemoveResult] Save completed. Result:', saveResult);
      console.log(`Result removed for fight. ${fightBets.length} bets (including parlays) reverted to pending.`);
    }
  };

  const handleMarkInProgress = async (fightId: string) => {
    if (!event) return;
    
    const fight = fights.find(f => f.id === fightId);
    if (!fight) return;
    
    const confirmation = window.confirm(
      `Mark ${fight.fighters[0]?.name} vs ${fight.fighters[1]?.name} as IN PROGRESS?\n\nThis will prevent new bets from being placed, but the fight is not yet finished.`
    );
    
    if (confirmation) {
      const updatedFights = fights.map(f =>
        f.id === fightId ? { ...f, status: 'in-progress' as const } : f
      );
      
      const updatedEvent = {
        ...event,
        fights: updatedFights,
      };
      
      updateEvent(updatedEvent);
      await saveEventOrTemplate(updatedEvent);
      
      console.log(`Fight marked as in progress: ${fightId}`);
    }
  };

  const handleRemoveInProgress = async (fightId: string) => {
    if (!event) return;
    
    const fight = fights.find(f => f.id === fightId);
    if (!fight) return;
    
    const confirmation = window.confirm(
      `Remove IN PROGRESS status from ${fight.fighters[0]?.name} vs ${fight.fighters[1]?.name}?\n\nThis will set it back to UPCOMING and allow bets again.`
    );
    
    if (confirmation) {
      const updatedFights = fights.map(f =>
        f.id === fightId ? { ...f, status: 'upcoming' as const } : f
      );
      
      const updatedEvent = {
        ...event,
        fights: updatedFights,
      };
      
      updateEvent(updatedEvent);
      await saveEventOrTemplate(updatedEvent);
      
      console.log(`Fight status reset to upcoming: ${fightId}`);
    }
  };

  const handleDeleteBet = async (betId: string, participantId: string) => {
    if (!event) return;
    if (!currentUser || !isUltraAdmin(currentUser)) {
      alert('Only super admins can delete bets.');
      return;
    }
    
    const bet = bets.find(b => b.id === betId);
    if (!bet) return;
    
    const participant = participants.find(p => p.id === participantId);
    if (!participant) return;
    
    const confirmation = window.confirm(
      `DELETE bet for ${participant.name}?\n\nAmount: ${formatCurrency(bet.amount)}\nStatus: ${bet.status}\n\nThis will refund the money to their balance. This action cannot be undone.`
    );
    
    if (confirmation) {
      // Remove bet from participant's bets array
      const updatedParticipants = event.participants.map(p => {
        if (p.id === participantId) {
          const updatedBets = p.bets.filter(b => b.id !== betId);
          
          // Refund the bet amount based on status
          let initialStakeAdjustment = 0;
          let winningsAdjustment = 0;
          
          if (bet.status === 'pending') {
            // Pending bet - refund from wherever it came from
            if (p.winningsBalance >= bet.amount) {
              winningsAdjustment = bet.amount;
            } else {
              const fromWinnings = p.winningsBalance;
              const fromStake = bet.amount - fromWinnings;
              winningsAdjustment = fromWinnings;
              initialStakeAdjustment = fromStake;
            }
          } else if (bet.status === 'lost') {
            // Lost bet - money is gone, just remove the bet record
            // No refund
          } else if (bet.status === 'won') {
            // Won bet - remove the winnings
            winningsAdjustment = -(bet.payout || 0) + bet.amount; // Remove payout, add back original bet
            initialStakeAdjustment = bet.amount; // Refund original amount to stake
          }
          
          return {
            ...p,
            bets: updatedBets,
            initialStakeRemaining: p.initialStakeRemaining + initialStakeAdjustment,
            winningsBalance: p.winningsBalance + winningsAdjustment,
            totalAvailable: p.totalAvailable + initialStakeAdjustment + winningsAdjustment
          };
        }
        return p;
      });
      
      const updatedEvent = {
        ...event,
        participants: updatedParticipants,
      };
      
      updateEvent(updatedEvent);
      await saveEventOrTemplate(updatedEvent);
      
      console.log(`Bet deleted: ${betId}, participant refunded`);
    }
  };

  const handleRunBetResolutionTests = async () => {
    if (!currentUser || !isUltraAdmin(currentUser)) {
      alert('Only super admins can run tests.');
      return;
    }
    
    console.log('🧪 Running bet resolution tests...');
    const summary = await runBetResolutionTests();
    
    const message = `BET RESOLUTION TEST RESULTS\n\n` +
      `Total Tests: ${summary.totalTests}\n` +
      `✅ Passed: ${summary.passed}\n` +
      `❌ Failed: ${summary.failed}\n\n` +
      `Check the browser console (F12) for detailed test output.`;
    
    if (summary.failed === 0) {
      alert(`✅ ALL TESTS PASSED!\n\n${message}`);
    } else {
      alert(`❌ SOME TESTS FAILED\n\n${message}`);
    }
  };

  const handleManualResolveBet = async (betId: string, participantId: string, resolution: 'won' | 'lost') => {
    if (!event) return;
    if (!currentUser || !isUltraAdmin(currentUser)) {
      alert('Only ultra admins can manually resolve bets.');
      return;
    }

    const confirmation = window.confirm(
      `Mark this bet as ${resolution.toUpperCase()}?\n\nThis action cannot be undone.`
    );
    if (!confirmation) return;

    try {
      const updatedParticipants = event.participants.map(p => {
        if (p.id !== participantId) return p;

        const updatedBets = p.bets.map(b => {
          if (b.id !== betId) return b;

          // Calculate payout for won bets
          let payout = 0;
          if (resolution === 'won') {
            const decimalOdds = b.odds >= 0 ? (b.odds / 100) + 1 : (100 / Math.abs(b.odds)) + 1;
            payout = b.amount * decimalOdds;
          }

          return {
            ...b,
            status: resolution,
            payout: resolution === 'won' ? payout : undefined,
          };
        });

        // Update participant balances for won bets
        const bet = p.bets.find(b => b.id === betId);
        if (!bet) return { ...p, bets: updatedBets };

        let winningsAdjustment = 0;
        if (resolution === 'won') {
          const decimalOdds = bet.odds >= 0 ? (bet.odds / 100) + 1 : (100 / Math.abs(bet.odds)) + 1;
          const payout = bet.amount * decimalOdds;
          winningsAdjustment = payout;
        }

        return {
          ...p,
          bets: updatedBets,
          winningsBalance: p.winningsBalance + winningsAdjustment,
          totalAvailable: p.totalAvailable + winningsAdjustment,
        };
      });

      const updatedEvent = {
        ...event,
        participants: updatedParticipants,
      };

      await saveEventOrTemplate(updatedEvent);
      updateEvent(updatedEvent);

      alert(`Bet marked as ${resolution}!`);
    } catch (error) {
      console.error('Error resolving bet:', error);
      alert('Failed to resolve bet. Please try again.');
    }
  };

  // Obsolete function removed - Cloud Functions now handle bet resolution automatically

  const handleRemoveParticipant = async (participantId: string) => {
    if (!event) return;
    
    const participant = participants.find(p => p.id === participantId);
    if (!participant) return;
    
    // Prevent removing the admin
    if (participant.id === event.adminId) {
      alert('Cannot remove the event administrator.');
      return;
    }
    
    // Check if participant has any bets
    const hasBets = participant.bets.length > 0;
    
    const confirmation = window.confirm(
      hasBets
        ? `Are you sure you want to remove ${participant.name}? They have ${participant.bets.length} bet(s) placed. All their bets will be removed.`
        : `Are you sure you want to remove ${participant.name}?`
    );
    
    if (confirmation) {
      // Remove participant from event
      const updatedParticipants = event.participants.filter(p => p.id !== participantId);
      
      const updatedEvent = {
        ...event,
        participants: updatedParticipants,
      };
      
      updateEvent(updatedEvent);
      await saveEventOrTemplate(updatedEvent);
      
      console.log(`Participant removed: ${participant.name}`);
    }
  };

  const handleMoveFightUp = async (fightIndex: number) => {
    if (!event || fightIndex === 0) return;
    
    const updatedFights = [...fights];
    // Swap with previous fight
    [updatedFights[fightIndex - 1], updatedFights[fightIndex]] = [updatedFights[fightIndex], updatedFights[fightIndex - 1]];
    
    const updatedEvent = {
      ...event,
      fights: updatedFights,
    };
    
    updateEvent(updatedEvent);
    await saveEventOrTemplate(updatedEvent);
    
    console.log(`Fight moved up: ${updatedFights[fightIndex - 1].id}`);
  };

  const handleMoveFightDown = async (fightIndex: number) => {
    if (!event || fightIndex === fights.length - 1) return;
    
    const updatedFights = [...fights];
    // Swap with next fight
    [updatedFights[fightIndex], updatedFights[fightIndex + 1]] = [updatedFights[fightIndex + 1], updatedFights[fightIndex]];
    
    const updatedEvent = {
      ...event,
      fights: updatedFights,
    };
    
    updateEvent(updatedEvent);
    await saveEventOrTemplate(updatedEvent);
    
    console.log(`Fight moved down: ${updatedFights[fightIndex + 1].id}`);
  };

  const handleOverrideBetResult = async (betId: string, newStatus: 'won' | 'lost' | 'push') => {
    if (!event) return;
    
    const confirmation = window.confirm(
      `Are you sure you want to manually override this bet result to "${newStatus.toUpperCase()}"?\n\nThis will recalculate the participant's balance accordingly.`
    );
    
    if (!confirmation) return;
    
    // Find the bet and participant
    const updatedParticipants = event.participants.map(participant => {
      const betIndex = participant.bets.findIndex(b => b.id === betId);
      if (betIndex === -1) return participant;
      
      const bet = participant.bets[betIndex];
      
      // Store original status if not already overridden
      if (!bet.overridden) {
        bet.originalStatus = bet.status;
      }
      
      // Update bet status
      bet.status = newStatus === 'push' ? 'pending' : newStatus;
      bet.overridden = true;
      
      // Recalculate payout
      if (newStatus === 'won') {
        // Calculate payout based on odds
        const decimalOdds = bet.odds > 0 
          ? 1 + (bet.odds / 100) 
          : 1 + (100 / Math.abs(bet.odds));
        bet.payout = bet.amount * decimalOdds;
      } else if (newStatus === 'push') {
        // Push = refund
        bet.payout = bet.amount;
      } else {
        // Lost
        bet.payout = 0;
      }
      
      // Recalculate participant balances
      let totalAvailable = 1000; // Starting amount
      let winningsBalance = 0;
      
      for (const b of participant.bets) {
        if (b.status === 'won') {
          const profit = (b.payout || 0) - b.amount;
          winningsBalance += profit;
          totalAvailable += profit;
        } else if (b.status === 'pending') {
          // Bet is still active (or was pushed)
          totalAvailable -= b.amount;
        } else {
          // Lost
          totalAvailable -= b.amount;
        }
      }
      
      return {
        ...participant,
        bets: [...participant.bets],
        totalAvailable,
        winningsBalance,
      };
    });
    
    const updatedEvent = {
      ...event,
      participants: updatedParticipants,
    };
    
    updateEvent(updatedEvent);
    await saveEventOrTemplate(updatedEvent);
    
    console.log(`Bet result overridden: ${betId} -> ${newStatus}`);
  };

  // Helper to format fight result
  const formatFightResult = (fight: typeof fights[0]): string | null => {
    if (!fight || !fight.result) return null;
    
    const result = fight.result;
    const winner = fight.fighters.find(f => f.id === result.winnerId);
    const winnerName = winner?.name || 'Unknown';
    
    let methodStr = '';
    switch (result.method) {
      case 'ko_tko': methodStr = 'KO/TKO'; break;
      case 'submission': methodStr = 'Submission'; break;
      case 'decision_unanimous': methodStr = 'UD'; break;
      case 'decision_split': methodStr = 'SD'; break;
      case 'decision_majority': methodStr = 'MD'; break;
      case 'dq': methodStr = 'DQ'; break;
      case 'draw': return 'Draw';
      case 'no_contest': return 'No Contest';
      default: methodStr = result.method;
    }
    
    if (result.wentTheDistance) {
      return `${winnerName} by ${methodStr}`;
    } else if (result.round && result.timeInRound) {
      return `${winnerName} by ${methodStr} (R${result.round}, ${result.timeInRound})`;
    } else if (result.round) {
      return `${winnerName} by ${methodStr} (R${result.round})`;
    }
    return `${winnerName} by ${methodStr}`;
  };

  return (
    <div className="min-h-screen bg-primary-bg pb-20">
      {/* Header */}
      <div className="bg-secondary-bg border-b border-border p-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-primary-text">
              {event.isTemplate ? 'Template Editor' : 'Event Admin'}
              <span className="text-xs text-green-500 ml-2">v2.1</span>
            </h1>
            <p className="text-sm text-secondary-text">
              {event.isTemplate ? `${event.sport || 'MMA'} Template: ${event.name}` : event.name}
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate(event.isTemplate ? `/ultra-admin/templates` : `/event/${eventId}`)}
          >
            {event.isTemplate ? 'Back to Templates' : 'View Dashboard'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-secondary-bg border-b border-border">
        <div className="max-w-4xl mx-auto px-4 flex gap-1 overflow-x-auto">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'overview'
                ? 'border-primary-blue text-primary-blue'
                : 'border-transparent text-secondary-text hover:text-primary-text'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('fights')}
            className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'fights'
                ? 'border-primary-blue text-primary-blue'
                : 'border-transparent text-secondary-text hover:text-primary-text'
            }`}
          >
            Fights ({fights.length})
          </button>
          {!event.isTemplate && (
            <>
              <button
                onClick={() => setActiveTab('participants')}
                className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === 'participants'
                    ? 'border-primary-blue text-primary-blue'
                    : 'border-transparent text-secondary-text hover:text-primary-text'
                }`}
              >
                Participants ({totalParticipants})
              </button>
              <button
                onClick={() => setActiveTab('bets')}
                className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === 'bets'
                    ? 'border-primary-blue text-primary-blue'
                    : 'border-transparent text-secondary-text hover:text-primary-text'
                }`}
              >
                Bets ({totalBets})
              </button>
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto p-4">
        {activeTab === 'overview' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card padding="md">
                <div className="text-sm text-secondary-text mb-1">Total Participants</div>
                <div className="text-2xl font-bold text-primary-text">{totalParticipants}</div>
              </Card>
              <Card padding="md">
                <div className="text-sm text-secondary-text mb-1">Total Bets</div>
                <div className="text-2xl font-bold text-primary-text">{totalBets}</div>
              </Card>
              <Card padding="md">
                <div className="text-sm text-secondary-text mb-1">Total Bet Amount</div>
                <div className="text-2xl font-bold text-primary-text">{formatCurrency(totalBetAmount)}</div>
              </Card>
            </div>

            <Card padding="lg">
              <h2 className="text-lg font-semibold text-primary-text mb-4">Event Status</h2>
              <div className="space-y-2 mb-4">
                <div className="flex justify-between">
                  <span className="text-secondary-text">Status:</span>
                  <Badge variant={event.status === 'upcoming' ? 'info' : event.status === 'live' ? 'error' : 'default'}>
                    {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-secondary-text">Published:</span>
                  <Badge variant={(event.published ?? true) ? 'success' : 'warning'}>
                    {(event.published ?? true) ? 'Published' : 'Draft (Unpublished)'}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-secondary-text">Created:</span>
                  <span className="text-primary-text">
                    {new Date(event.createdAt).toLocaleString()}
                  </span>
                </div>
              </div>
            </Card>

            {/* Shareable Link Display */}
            <ShareableLinkDisplay event={event} />

            <Card padding="lg">
              <h2 className="text-lg font-semibold text-primary-text mb-4">
                {event.isTemplate ? 'Template Actions' : 'Event Actions'}
              </h2>
              <div className="space-y-2">
                {/* Test Bet Resolution (Ultra-Admin Only) */}
                {userIsUltraAdmin && (
                  <Button
                    variant="secondary"
                    fullWidth
                    onClick={handleRunBetResolutionTests}
                  >
                    🧪 Test Bet Resolution System
                  </Button>
                )}
                
                
                {/* Publish/Unpublish Button */}
                <Button
                  variant={(event.published ?? true) ? 'secondary' : 'success'}
                  fullWidth
                  onClick={handlePublishEvent}
                >
                  {(event.published ?? true) 
                    ? (event.isTemplate ? 'Unpublish Template' : 'Unpublish Event')
                    : (event.isTemplate ? 'Publish Template' : 'Publish Event')}
                </Button>
                
                {/* Fight Management - Only for Templates */}
                {canUserManageFights && (
                  <>
                    <Button
                      variant="primary"
                      fullWidth
                      onClick={() => navigate(`/admin/${eventId}/fights/new`)}
                    >
                      Add Fight
                    </Button>
                    <Button
                      variant="secondary"
                      fullWidth
                      onClick={() => setActiveTab('fights')}
                    >
                      Manage Fights
                    </Button>
                  </>
                )}
                
                {/* Participant Management - Only for Event Instances */}
                {canUserManageParticipants && (
                    <Button
                    variant="secondary"
                    fullWidth
                    onClick={() => setActiveTab('participants')}
                  >
                    View Participants
                  </Button>
                )}
                
                {/* Screencast Leaderboard - Only for Event Instances (not templates) */}
                {!event.isTemplate && (
                  <Button
                    variant="secondary"
                    fullWidth
                    onClick={() => navigate(`/screencast/${eventId}`)}
                  >
                    Screencast Leaderboard
                  </Button>
                )}
                {/* Finish Template - Only for Templates (ultra-admin only) */}
                {event.isTemplate && !event.finished && userIsUltraAdmin && (
                  <Button
                    variant="danger"
                    fullWidth
                    onClick={handleFinishTemplate}
                  >
                    Finish Template (& All Events)
                  </Button>
                )}
                {event.finished && (
                  <div className="p-3 bg-success-green/10 border border-success-green rounded-lg">
                    <p className="text-sm text-success-green font-medium">
                      {event.isTemplate ? 'Template' : 'Event'} finished on {event.finishedAt ? new Date(event.finishedAt).toLocaleString() : 'Unknown'}
                    </p>
                  </div>
                )}
                
                {/* Delete Button - Only for Events (not templates) */}
                {!event.isTemplate && (
                  <div className="pt-4 border-t border-border">
                    <Button
                      variant="danger"
                      fullWidth
                      onClick={handleDeleteEvent}
                    >
                      Delete Event
                    </Button>
                  </div>
                )}
                
                {event.isTemplate && (
                  <div className="pt-4 border-t border-border">
                    <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                      <p className="text-xs text-yellow-400">
                        Delete templates from the Templates page
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </Card>

          </div>
        )}

        {activeTab === 'fights' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-primary-text">
                {event.isTemplate ? 'Manage Fights' : 'Fights (From Template)'}
              </h2>
              {canUserManageFights && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate(`/admin/${eventId}/fights/new`)}
                >
                  Add Fight
                </Button>
              )}
            </div>
            
            {!event.isTemplate && fights.length > 0 && (
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg mb-4">
                <p className="text-sm text-blue-400">
                  💡 Fights are managed through the template. Results entered by ultra-admin will appear here automatically.
                </p>
              </div>
            )}
            
            {fights.length === 0 ? (
              <Card padding="lg" className="text-center">
                <p className="text-secondary-text mb-4">
                  {event.isTemplate ? 'No fights added to this template yet.' : 'This event uses a template. Fights are managed by the ultra-admin on the template.'}
                </p>
                {canUserManageFights && (
                  <Button
                    variant="primary"
                    onClick={() => navigate(`/admin/${eventId}/fights/new`)}
                  >
                    Add First Fight to Template
                  </Button>
                )}
              </Card>
            ) : (
              <div className="space-y-4">
                {fights.map((fight, index) => {
                  // Count both regular bets AND parlays with legs on this fight
                  const fightBetsCount = bets.filter(b => 
                    b.fightId === fight.id || 
                    (b.isParlay && b.parlayLegs?.some(leg => leg.fightId === fight.id))
                  ).length;
                  
                  return (
                    <Card key={fight.id} padding="md">
                      <div className="flex gap-3 items-start mb-3">
                        {/* Fight Number & Reorder Buttons (Templates Only, Ultra-Admin Only) */}
                        {canUserManageFights && event.isTemplate && (
                          <div className="flex flex-col items-center gap-1 pt-1">
                            <span className="text-xs font-bold text-secondary-text">#{index + 1}</span>
                            <div className="flex flex-col gap-1">
                              <button
                                onClick={() => handleMoveFightUp(index)}
                                disabled={index === 0}
                                className={`p-1 rounded transition-colors ${
                                  index === 0 
                                    ? 'text-secondary-text/30 cursor-not-allowed' 
                                    : 'text-secondary-text hover:text-primary-text hover:bg-tertiary-bg'
                                }`}
                                title="Move up"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleMoveFightDown(index)}
                                disabled={index === fights.length - 1}
                                className={`p-1 rounded transition-colors ${
                                  index === fights.length - 1 
                                    ? 'text-secondary-text/30 cursor-not-allowed' 
                                    : 'text-secondary-text hover:text-primary-text hover:bg-tertiary-bg'
                                }`}
                                title="Move down"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        )}
                        
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="text-lg font-semibold text-primary-text">
                                {fight.fighters[0]?.name || 'TBD'} vs {fight.fighters[1]?.name || 'TBD'}
                              </h3>
                              <div className="flex gap-2 items-center mt-1">
                                <Badge variant={
                                  fight.status === 'upcoming' ? 'info' : 
                                  fight.status === 'in-progress' ? 'warning' : 
                                  fight.status === 'live' ? 'error' : 
                                  'default'
                                }>
                                  {fight.status === 'in-progress' ? 'In Progress' : fight.status.charAt(0).toUpperCase() + fight.status.slice(1)}
                                </Badge>
                                {fightBetsCount > 0 && (
                                  <span className="text-xs text-secondary-text">
                                    {fightBetsCount} bet{fightBetsCount !== 1 ? 's' : ''}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2 flex-wrap justify-end">
                          {/* Result Management - Only for Templates */}
                          {canUserEnterResults && (
                            <>
                              {fight.status === 'upcoming' && (
                                <>
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => handleMarkInProgress(fight.id)}
                                  >
                                    Mark In Progress
                                  </Button>
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => navigate(`/admin/${eventId}/fights/${fight.id}/result`)}
                                  >
                                    Enter Result
                                  </Button>
                                </>
                              )}
                              {fight.status === 'in-progress' && (
                                <>
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => handleRemoveInProgress(fight.id)}
                                  >
                                    Remove In Progress
                                  </Button>
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => navigate(`/admin/${eventId}/fights/${fight.id}/result`)}
                                  >
                                    Enter Result
                                  </Button>
                                </>
                              )}
                              {fight.status === 'completed' && (
                                <>
                                  {currentUser && isUltraAdmin(currentUser) && (
                                    <Button
                                      variant="success"
                                      size="sm"
                                      onClick={() => setSelectedFightForBetAnalysis(
                                        selectedFightForBetAnalysis === fight.id ? null : fight.id
                                      )}
                                    >
                                      {selectedFightForBetAnalysis === fight.id ? 'Hide' : 'Bet Analysis'}
                                    </Button>
                                  )}
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => navigate(`/admin/${eventId}/fights/${fight.id}/result`)}
                                  >
                                    Edit Result
                                  </Button>
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => handleRemoveResult(fight.id)}
                                  >
                                    Remove Result
                                  </Button>
                                </>
                              )}
                            </>
                          )}
                          
                          {/* Fight Management - Only for Templates */}
                          {canUserManageFights && (
                            <>
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => navigate(`/admin/${eventId}/fights/${fight.id}`)}
                              >
                                Edit Odds
                              </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleDeleteFight(fight.id)}
                              >
                                Delete
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {/* Moneyline Odds */}
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        {fight.fighters.map((fighter) => {
                          const fighterBets = bets.filter(b => 
                            b.fightId === fight.id && 
                            b.fighterId === fighter.id && 
                            !b.isParlay
                          );
                          const totalAmount = fighterBets.reduce((sum, b) => sum + b.amount, 0);
                          
                          return (
                            <div key={fighter.id} className="bg-tertiary-bg p-3 rounded-lg">
                              <div className="font-medium text-primary-text">{fighter.name}</div>
                              <div className="text-sm text-secondary-text mt-1">
                                Odds: {fighter.odds > 0 ? '+' : ''}{fighter.odds}
                              </div>
                              {fighterBets.length > 0 && (
                                <div className="text-xs text-blue-400 mt-1">
                                  {fighterBets.length} bet{fighterBets.length !== 1 ? 's' : ''} • {formatCurrency(totalAmount)}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Prop Bets / Markets */}
                      {fight.markets && fight.markets.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-border">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-semibold text-primary-text">Prop Bets</h4>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => setSelectedFightForDetails(
                                selectedFightForDetails === fight.id ? null : fight.id
                              )}
                            >
                              {selectedFightForDetails === fight.id ? 'Hide Details' : 'View All Bets'}
                            </Button>
                          </div>
                          
                          {selectedFightForDetails === fight.id && (
                            <div className="space-y-3">
                              {fight.markets.map((market) => (
                                <div key={market.key} className="bg-secondary-bg p-3 rounded-lg">
                                  <div className="font-medium text-primary-text mb-2">{market.name}</div>
                                  <div className="space-y-2">
                                    {market.outcomes.map((outcome) => {
                                      const outcomeBets = bets.filter(b => 
                                        b.fightId === fight.id && 
                                        b.marketKey === market.key && 
                                        b.outcomeId === outcome.id &&
                                        !b.isParlay
                                      );
                                      const totalAmount = outcomeBets.reduce((sum, b) => sum + b.amount, 0);
                                      
                                      return (
                                        <div key={outcome.id} className="flex items-center justify-between bg-tertiary-bg p-2 rounded">
                                          <div>
                                            <div className="text-sm text-primary-text">{outcome.name}</div>
                                            {outcomeBets.length > 0 && (
                                              <div className="text-xs text-blue-400">
                                                {outcomeBets.length} bet{outcomeBets.length !== 1 ? 's' : ''} • {formatCurrency(totalAmount)}
                                              </div>
                                            )}
                                          </div>
                                          <div className="text-sm text-secondary-text font-mono">
                                            {outcome.odds > 0 ? '+' : ''}{outcome.odds}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Bet Analysis (Ultra-Admin Only, Completed Fights) */}
                      {selectedFightForBetAnalysis === fight.id && fight.status === 'completed' && currentUser && isUltraAdmin(currentUser) && (
                        <div className="mt-4 pt-4 border-t border-border">
                          <h4 className="text-sm font-semibold text-primary-text mb-3">
                            🔍 Bet Analysis & Override
                          </h4>
                          
                          {(() => {
                            // Include both regular bets AND parlays with legs on this fight
                            const fightBets = bets.filter(b => 
                              b.fightId === fight.id || 
                              (b.isParlay && b.parlayLegs?.some(leg => leg.fightId === fight.id))
                            );
                            
                            if (fightBets.length === 0) {
                              return (
                                <div className="text-sm text-secondary-text text-center py-4">
                                  No bets placed on this fight
                                </div>
                              );
                            }
                            
                            return (
                              <div className="space-y-2">
                                {fightBets.map(bet => {
                                  const participant = participants.find(p => p.id === bet.participantId);
                                  const betDescription = bet.isParlay 
                                    ? `Parlay (${bet.parlayLegs?.length || 0} legs)` 
                                    : bet.fighterId 
                                      ? fight.fighters.find(f => f.id === bet.fighterId)?.name || 'Unknown Fighter'
                                      : bet.outcomeName || 'Unknown Outcome';
                                  
                                  return (
                                    <div 
                                      key={bet.id} 
                                      className={`bg-secondary-bg p-3 rounded-lg border ${
                                        bet.overridden 
                                          ? 'border-yellow-500/50' 
                                          : bet.status === 'won' 
                                            ? 'border-success-green/30' 
                                            : bet.status === 'lost' 
                                              ? 'border-red-500/30' 
                                              : 'border-border'
                                      }`}
                                    >
                                      <div className="flex justify-between items-start mb-2">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-primary-text">
                                              {participant?.name || 'Unknown'}
                                            </span>
                                            {bet.overridden && (
                                              <Badge variant="warning" className="text-xs">
                                                OVERRIDDEN
                                              </Badge>
                                            )}
                                            <Badge variant={
                                              bet.status === 'won' ? 'success' : 
                                              bet.status === 'lost' ? 'error' : 
                                              'default'
                                            }>
                                              {bet.status.toUpperCase()}
                                            </Badge>
                                          </div>
                                          <div className="text-xs text-secondary-text mt-1">
                                            {betDescription}
                                          </div>
                                          {bet.marketKey && (
                                            <div className="text-xs text-secondary-text">
                                              Market: {bet.marketKey}
                                            </div>
                                          )}
                                        </div>
                                        <div className="text-right">
                                          <div className="text-sm font-medium text-primary-text">
                                            {formatCurrency(bet.amount)}
                                          </div>
                                          <div className="text-xs text-secondary-text">
                                            {bet.odds > 0 ? '+' : ''}{bet.odds}
                                          </div>
                                          {bet.payout !== undefined && bet.status === 'won' && (
                                            <div className="text-xs text-success-green mt-1">
                                              Win: {formatCurrency(bet.payout)}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      
                                      {bet.overridden && bet.originalStatus && (
                                        <div className="text-xs text-yellow-400 mb-2">
                                          ⚠️ Originally calculated as: {bet.originalStatus.toUpperCase()}
                                        </div>
                                      )}
                                      
                                      {/* Show fight result for resolved bets */}
                                      {(bet.status === 'won' || bet.status === 'lost') && !bet.isParlay && (
                                        <div className="text-xs text-muted-text mt-1">
                                          Result: {formatFightResult(fight) || 'N/A'}
                                        </div>
                                      )}
                                      
                                      {/* Show parlay legs with results */}
                                      {bet.isParlay && bet.parlayLegs && bet.parlayLegs.length > 0 && (
                                        <div className="mt-2 pt-2 border-t border-border/30">
                                          <div className="text-xs font-medium text-secondary-text mb-1">Parlay Legs:</div>
                                          <div className="space-y-1">
                                            {bet.parlayLegs.map((leg, idx) => {
                                              const legFight = fights.find(f => f.id === leg.fightId);
                                              const legResult = legFight ? formatFightResult(legFight) : null;
                                              return (
                                                <div key={idx} className="text-xs text-secondary-text flex items-center gap-1">
                                                  <span className={
                                                    leg.status === 'won' ? 'text-success-green' : 
                                                    leg.status === 'lost' ? 'text-error-red' : 
                                                    'text-warning-yellow'
                                                  }>
                                                    {leg.status === 'won' ? '✓' : leg.status === 'lost' ? '✗' : '⏳'}
                                                  </span>
                                                  <span>{leg.selection}</span>
                                                  {legResult && <span className="text-muted-text">({legResult})</span>}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Override Buttons */}
                                      <div className="flex gap-2 mt-3 pt-3 border-t border-border/50">
                                        <Button
                                          variant={bet.status === 'won' && !bet.overridden ? 'success' : 'secondary'}
                                          size="sm"
                                          onClick={() => handleOverrideBetResult(bet.id, 'won')}
                                          disabled={bet.status === 'won' && !bet.overridden}
                                          fullWidth
                                        >
                                          Mark Won
                                        </Button>
                                        <Button
                                          variant={bet.status === 'lost' && !bet.overridden ? 'danger' : 'secondary'}
                                          size="sm"
                                          onClick={() => handleOverrideBetResult(bet.id, 'lost')}
                                          disabled={bet.status === 'lost' && !bet.overridden}
                                          fullWidth
                                        >
                                          Mark Lost
                                        </Button>
                                        <Button
                                          variant="secondary"
                                          size="sm"
                                          onClick={() => handleOverrideBetResult(bet.id, 'push')}
                                          fullWidth
                                        >
                                          Mark Push
                                        </Button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                          </div>
                        </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'participants' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-primary-text">Participants</h2>
            {participants.length === 0 ? (
              <Card padding="lg" className="text-center">
                <p className="text-secondary-text">No participants yet.</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {participants.map((participant) => (
                  <Card key={participant.id} padding="md">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-primary-text">{participant.name}</h3>
                        <div className="text-sm text-secondary-text mt-1">
                          Joined: {new Date(participant.joinedAt).toLocaleString()}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-secondary-text">Balance</div>
                        <div className="text-lg font-bold text-success-green">
                          {formatCurrency(participant.totalAvailable)}
                        </div>
                        <div className="text-xs text-secondary-text mt-1">
                          Winnings: {formatCurrency(participant.winningsBalance)}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-border">
                      <div className="flex justify-between items-center">
                        <div className="flex justify-between text-sm flex-1 mr-4">
                          <span className="text-secondary-text">Total Bets:</span>
                          <span className="text-primary-text">{participant.bets.length}</span>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => navigate(`/admin/${eventId}/participants/${participant.id}`)}
                          >
                            Review Bets
                          </Button>
                          {participant.id !== event.adminId && (
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleRemoveParticipant(participant.id)}
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'bets' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-primary-text">All Bets</h2>
            {bets.length === 0 ? (
              <Card padding="lg" className="text-center">
                <p className="text-secondary-text">No bets placed yet.</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {bets.map((bet) => {
                  const participant = participants.find((p) => p.id === bet.participantId);
                  const fight = fights.find((f) => f.id === bet.fightId);
                  
                  // Determine bet description
                  let betDescription = '';
                  let betDetails = '';
                  
                  if (bet.isParlay) {
                    betDescription = `${bet.parlayLegs?.length || 0}-Leg Parlay`;
                    betDetails = `${fight?.fighters[0]?.name || 'TBD'} vs ${fight?.fighters[1]?.name || 'TBD'} (+ others)`;
                  } else if (bet.fighterId) {
                    // Moneyline bet
                    const fighter = fight?.fighters.find((f) => f.id === bet.fighterId);
                    betDescription = fighter?.name || 'Unknown Fighter';
                    betDetails = `${fight?.fighters[0]?.name || 'TBD'} vs ${fight?.fighters[1]?.name || 'TBD'}`;
                  } else if (bet.outcomeName) {
                    // Prop bet
                    betDescription = bet.outcomeName;
                    betDetails = `${fight?.fighters[0]?.name || 'TBD'} vs ${fight?.fighters[1]?.name || 'TBD'}`;
                    if (bet.marketKey) {
                      betDetails += ` • ${bet.marketKey}`;
                    }
                  } else {
                    betDescription = 'Unknown Bet';
                    betDetails = `${fight?.fighters[0]?.name || 'TBD'} vs ${fight?.fighters[1]?.name || 'TBD'}`;
                  }
                  
                  return (
                    <Card key={bet.id} padding="md">
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1">
                          <div className="font-semibold text-primary-text">{participant?.name || 'Unknown'}</div>
                          <div className="text-sm text-primary-text mt-1 font-medium">
                            {betDescription}
                          </div>
                          <div className="text-sm text-secondary-text">
                            {betDetails}
                          </div>
                          <div className="text-xs text-muted-text mt-1">
                            {new Date(bet.placedAt).toLocaleString()} • Odds: {bet.odds > 0 ? '+' : ''}{bet.odds}
                          </div>
                          {bet.payout && bet.status === 'won' && (
                            <div className="text-xs text-success-green mt-1">
                              Payout: {formatCurrency(bet.payout)}
                            </div>
                          )}
                          
                          {/* Show fight result for resolved non-parlay bets */}
                          {(bet.status === 'won' || bet.status === 'lost') && !bet.isParlay && fight && (
                            <div className="text-xs text-muted-text mt-1">
                              Result: {formatFightResult(fight) || 'N/A'}
                            </div>
                          )}
                          
                          {/* Parlay Legs Display */}
                          {bet.isParlay && bet.parlayLegs && bet.parlayLegs.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-border">
                              <div className="text-xs font-semibold text-primary-text mb-2">Parlay Legs:</div>
                              <div className="space-y-1">
                                {bet.parlayLegs.map((leg, index) => {
                                  const legFight = fights.find(f => f.id === leg.fightId);
                                  const legResult = legFight ? formatFightResult(legFight) : null;
                                  return (
                                    <div 
                                      key={index}
                                      className="text-xs"
                                    >
                                      <div className="flex items-center gap-2">
                                        {leg.status === 'won' && <span className="text-success-green">✓</span>}
                                        {leg.status === 'lost' && <span className="text-error">✗</span>}
                                        {leg.status === 'pending' && <span className="text-muted-text">○</span>}
                                        <span className="text-secondary-text">
                                          {leg.selection} ({leg.odds > 0 ? '+' : ''}{leg.odds})
                                        </span>
                                      </div>
                                      {legResult && (leg.status === 'won' || leg.status === 'lost') && (
                                        <div className="text-muted-text ml-5">
                                          Result: {legResult}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="text-right flex items-start gap-3">
                          <div>
                            <div className="text-sm text-secondary-text">Bet Amount</div>
                            <div className="text-lg font-bold text-primary-text">
                              {formatCurrency(bet.amount)}
                            </div>
                            <Badge
                              variant={
                                bet.status === 'won' ? 'success' :
                                bet.status === 'lost' ? 'error' :
                                'warning'
                              }
                              size="sm"
                              className="mt-2"
                            >
                              {bet.status.charAt(0).toUpperCase() + bet.status.slice(1)}
                            </Badge>
                          </div>
                          {currentUser && isUltraAdmin(currentUser) && (
                            <div className="flex flex-col gap-2">
                              {bet.status === 'pending' && (
                                <div className="flex gap-2">
                                  <Button
                                    variant="success"
                                    size="sm"
                                    onClick={() => handleManualResolveBet(bet.id, bet.participantId, 'won')}
                                  >
                                    ✓ Won
                                  </Button>
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => handleManualResolveBet(bet.id, bet.participantId, 'lost')}
                                  >
                                    ✗ Lost
                                  </Button>
                                </div>
                              )}
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleDeleteBet(bet.id, bet.participantId)}
                              >
                                Delete
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

